import { useMemo } from "react";
import { TYPE_FIELD_LIST } from "src/helper/TypeFieldList";
import { v4 as uuid } from "uuid";
import * as yup from "yup";
import { MESSAGE } from "src/message";

export default function useProviderSchema({ userMe, currAddLevel }) {
  const providerFieldList = useMemo(
    () => [
      {
        id: uuid(),
        name: "name",
        label: "Tên nhà cung cấp",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: MESSAGE.ORIGIN_NAME,
      },
      {
        id: uuid(),
        name: "email",
        label: "Email",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: MESSAGE.ORIGIN_NAME,
      },
      {
        id: uuid(),
        name: "phone",
        label: "SĐT",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: MESSAGE.ORIGIN_NAME,
      },
      {
        id: uuid(),
        name: "taxCode",
        label: "Mã số thuế",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: MESSAGE.ORIGIN_NAME,
      },
      {
        id: uuid(),
        name: "country",
        label: "Quốc gia",
        typeField: TYPE_FIELD_LIST.DYNAMIC_SELECT,
        path: "/api/v1/countries",
        params: { limit: 20, skip: 0, searchField: "name", sortField: "order", sortOrder: "asc" },
        type: "text",
        defaultValue: "",
        grid: [12],
        required: true,
        initialShow: true,
      },
      {
        id: uuid(),
        name: "detailAddress",
        label: "Địa chỉ",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: MESSAGE.ORIGIN_NAME,
      },
      {
        id: uuid(),
        name: "addressLevel",
        label: "Cấp địa chỉ",
        type: "radio",
        grid: [12],
        typeField: TYPE_FIELD_LIST.RADIO,
        placeHolder: MESSAGE.ORIGIN_NAME,
        defaultValue: "levelTwo",
        child: [
          {
            id: uuid(),
            name: "levelTwo",
            label: "Địa chỉ 2 cấp",
            typeField: TYPE_FIELD_LIST.RADIO,
            type: "text",
            defaultValue: "",
            grid: [6],
            placeHolder: MESSAGE.ORIGIN_NAME,
          },
          {
            id: uuid(),
            name: "levelThree",
            label: "Địa chỉ 3 cấp",
            typeField: TYPE_FIELD_LIST.RADIO,
            type: "text",
            defaultValue: "",
            grid: [6],
            placeHolder: MESSAGE.ORIGIN_NAME,
          },
        ],
      },
      {
        id: uuid(),
        name: "province",
        label: "Thành phố/ Tỉnh",
        typeField: TYPE_FIELD_LIST.DYNAMIC_SELECT,
        path: "/api/v1/addresses/provinces",
        params: { limit: 200, skip: 0, searchField: "name" },
        type: "text",
        defaultValue: "",
        grid: currAddLevel === "levelTwo" ? [6, 6] : [4, 4],
        placeHolder: MESSAGE.ORIGIN_NAME,
      },
      {
        id: uuid(),
        name: "district",
        label: "Quận/ Huyện",
        typeField: TYPE_FIELD_LIST.DYNAMIC_SELECT,
        path: "/api/v1/addresses/districts",
        params: { limit: 200, skip: 0, searchField: "name" },
        type: "text",
        defaultValue: "",
        grid: currAddLevel === "levelTwo" ? [6, 6] : [4, 4],
        placeHolder: MESSAGE.ORIGIN_NAME,
        disabledDependsOn: "province",
        display: currAddLevel !== "levelTwo",
      },
      {
        id: uuid(),
        name: "ward",
        label: "Phường/ Xã",
        typeField: TYPE_FIELD_LIST.DYNAMIC_SELECT,
        path: "/api/v1/addresses/wards",
        params: { limit: 200, skip: 0, searchField: "name" },
        type: "text",
        defaultValue: {},
        grid: currAddLevel === "levelTwo" ? [6, 6] : [4, 4],
        placeHolder: MESSAGE.ORIGIN_NAME,
        disabledDependsOn: currAddLevel === "levelTwo" ? "province" : "district",
      },
      {
        id: uuid(),
        name: "note",
        label: "Ghi chú",
        typeField: TYPE_FIELD_LIST.TEXTFIELD,
        type: "text",
        defaultValue: "",
        grid: [12],
        placeHolder: MESSAGE.ORIGIN_NAME,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userMe?.warehouse, currAddLevel],
  );

  const isVietnam = (country) =>
    ["vietnam", "việt nam"].includes(
      String(country?.name ?? "")
        .trim()
        .toLowerCase(),
    );

  const validationSchema = yup.object().shape({
    name: yup.string().required("Vui lòng nhập tên nhà cung cấp"),
    email: yup.string().trim().email("Email không đúng định dạng").required("Vui lòng nhập email"),
    phone: yup
      .string()
      .required("Vui lòng nhập số điện thoại")
      .matches(/^[0-9]{10,11}$/, "Số điện thoại không hợp lệ"),
    taxCode: yup.string().required("Vui lòng nhập mã số thuế").matches(/^\d+$/, "Mã số thuế chỉ được chứa số").min(6, "Mã số thuế phải nhiều hơn 5 ký tự"),
    detailAddress: yup.string().required("Vui lòng nhập địa chỉ chi tiết"),
    country: yup.object().required("Vui lòng chọn quốc gia"),
    province: yup.mixed().when("country", {
      is: (country) => isVietnam(country),
      then: (schema) => schema.required("Vui lòng chọn Thành phố/ Tỉnh"),
      otherwise: (schema) => schema.notRequired(),
    }),
    district: yup.mixed().when(["country", "addressLevel"], {
      is: (country, addressLevel) => isVietnam(country) && addressLevel === "levelThree",
      then: (schema) => schema.required("Vui lòng chọn Quận/ Huyện"),
      otherwise: (schema) => schema.notRequired(),
    }),
    ward: yup.mixed().when("country", {
      is: (country) => isVietnam(country),
      then: (schema) => schema.required("Vui lòng chọn Phường/ Xã"),
      otherwise: (schema) => schema.notRequired(),
    }),
    addressLevel: yup.string().when("country", {
      is: (country) => isVietnam(country),
      then: (schema) => schema.required("Vui lòng chọn cấp địa chỉ"),
      otherwise: (schema) => schema.notRequired(),
    }),
    note: yup.string().nullable().max(500, "Không nhập quá 500 ký tự"),
  });
  const providerObject = {
    name: "",
    email: "",
    phone: "",
    taxCode: "",
    detailAddress: "",
    country: undefined,
    addressLevel: "levelTwo",
    note: "",
    ward: undefined,
    province: undefined,
    district: undefined,
  };
  return { providerObject, providerFieldList, validationSchema };
}
